import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import {
  getSearchHarvestReport,
  type SearchHarvestHit,
  type SearchHarvestReport,
  type SearchHarvestRetailerSummary,
} from "../../services/SearchHarvestService";

// Admin → Monitor → Search harvest. What users' Walmart / Home Depot product
// searches turned into: 60%+ in-stock hits (from the searches themselves and
// from the nightly Tempo re-checks at other users' areas), what the crawler
// had missed, and what people search most.

type View = "hits" | "gaps" | "top";
type Retailer = "walmart" | "home_depot";

const RETAILER_LABEL: Record<Retailer, string> = { walmart: "Walmart", home_depot: "Home Depot" };
const CARD = "bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm";
const LABEL = "text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400";

const money = (v: number) => `$${v.toFixed(2)}`;
const fmtTime = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

function itemUrl(retailer: string, code: string): string {
  return retailer === "home_depot" ? `https://www.homedepot.com/p/${code}` : `https://www.walmart.com/ip/${code}`;
}

export default function SearchHarvestTab() {
  const [view, setView] = useState<View>("hits");
  const [retailer, setRetailer] = useState<Retailer>("walmart");
  const [followedOnly, setFollowedOnly] = useState(true);
  const [search, setSearch] = useState("");

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-search-harvest"],
    queryFn: () => getSearchHarvestReport(false),
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-dark-400 py-10 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading the latest report…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="rounded-2xl ring-1 ring-amber-200 dark:ring-amber-900/40 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-800 dark:text-amber-200 flex gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">No report yet</div>
          <div className="mt-1 opacity-80">
            {error instanceof Error ? error.message : "The daily job has not published a report."} It runs every morning on the monitoring host
            (<code>search-harvest -s3 -discord</code>).
          </div>
        </div>
      </div>
    );
  }

  const summary = data.retailers?.[retailer];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white">Search harvest</h2>
          <p className="text-xs text-gray-500 dark:text-dark-300 mt-0.5">
            Built {fmtTime(data.generatedAt)} · searches since {data.windowStart} ET · hits are {data.minPct}%+ off with stock ·{" "}
            {(data.rescanToday?.lookups ?? data.rescan.lookups).toLocaleString()} Tempo re-checks today across {data.rescan.areas} areas
            {data.rescanToday?.errors ? ` (${data.rescanToday.errors} errors)` : ""}
            {data.rescan.stopped ? ` · last run ${data.rescan.stopped}` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 dark:text-dark-200 ring-1 ring-gray-200 dark:ring-dark-600 bg-white dark:bg-dark-800 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(Object.keys(RETAILER_LABEL) as Retailer[]).map((r) => (
          <Segment key={r} active={retailer === r} onClick={() => setRetailer(r)}>
            {RETAILER_LABEL[r]}
          </Segment>
        ))}
      </div>

      {summary && <SummaryStrip summary={summary} />}

      <div className={`${CARD} px-3 py-2 flex flex-wrap items-center gap-2`}>
        <Segment active={view === "hits"} onClick={() => setView("hits")}>
          Hits
        </Segment>
        <Segment active={view === "gaps"} onClick={() => setView("gaps")}>
          Crawler gaps
        </Segment>
        <Segment active={view === "top"} onClick={() => setView("top")}>
          Most searched
        </Segment>
        <div className="flex-1" />
        {view === "hits" && (
          <label className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-dark-300">
            <input type="checkbox" checked={followedOnly} onChange={(e) => setFollowedOnly(e.target.checked)} />
            Followed stores only
          </label>
        )}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, item #, store #"
          className="w-full sm:w-56 px-2.5 py-1.5 text-xs bg-white dark:bg-dark-800 ring-1 ring-gray-200 dark:ring-dark-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-dark-500"
        />
      </div>

      {view === "hits" && <HitsView data={data} retailer={retailer} followedOnly={followedOnly} search={search} />}
      {view === "gaps" && <GapsView data={data} retailer={retailer} search={search} />}
      {view === "top" && <TopView data={data} retailer={retailer} search={search} />}
    </div>
  );
}

function SummaryStrip({ summary: s }: { summary: SearchHarvestRetailerSummary }) {
  const coverage = s.gaps + s.known > 0 ? `${s.coveragePct}%` : "—";
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <Stat label="Searches" value={s.searches.toLocaleString()} sub={`${s.uniqueItems.toLocaleString()} items · ${s.freshSearches.toLocaleString()} live lookups`} />
      <Stat label="Hits" value={(s.searchHits + s.rescanHits).toLocaleString()} sub={`${s.searchHits} from searches · ${s.rescanHits} from re-checks`} />
      <Stat label="At followed stores" value={s.hitsAtFollowed.toLocaleString()} sub={`${s.activeStores.toLocaleString()} followed stores`} />
      <Stat label="New catalog rows" value={s.newRows.toLocaleString()} sub="deals the feed didn't have" />
      <Stat label="Crawler coverage" value={coverage} sub={`${s.known} already crawled · ${s.gaps} missed`} />
    </div>
  );
}

function matches(q: string, ...fields: (string | undefined)[]) {
  if (!q) return true;
  return fields.some((f) => (f ?? "").toLowerCase().includes(q));
}

function HitsView({ data, retailer, followedOnly, search }: { data: SearchHarvestReport; retailer: Retailer; followedOnly: boolean; search: string }) {
  const q = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      (data.hits ?? []).filter(
        (h) => h.retailer === retailer && (!followedOnly || h.activeStore) && matches(q, h.name, h.code, h.storeId, h.barcode),
      ),
    [data.hits, retailer, followedOnly, q],
  );
  if (rows.length === 0) return <Empty text="No hits match. Hits appear once users search items that are 60%+ off with stock." />;
  return (
    <div className={`${CARD} overflow-hidden divide-y divide-gray-100 dark:divide-dark-700`}>
      <div className="px-3 py-2 flex items-center justify-between">
        <p className={LABEL}>{rows.length} hits</p>
        <p className={LABEL}>price · stock</p>
      </div>
      {rows.map((h) => (
        <HitRow key={`${h.retailer}|${h.code}|${h.storeId}`} hit={h} />
      ))}
    </div>
  );
}

function HitRow({ hit: h }: { hit: SearchHarvestHit }) {
  const status = !h.activeStore
    ? { text: "Not followed", tone: "bg-gray-100 text-gray-500 dark:bg-dark-700 dark:text-dark-300" }
    : h.saveError
      ? { text: "Save failed", tone: "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300" }
      : !h.inCatalog
        ? { text: h.added ? "Added · crawler missed" : "Crawler missed", tone: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" }
        : { text: "Already crawled", tone: "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300" };
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <Thumb src={h.image} className="w-11 h-11 rounded-lg p-0.5" />
      <div className="min-w-0 flex-1">
        <a
          href={itemUrl(h.retailer, h.code)}
          target="_blank"
          rel="noreferrer"
          className="text-[12.5px] font-semibold leading-snug text-gray-900 dark:text-white line-clamp-1 hover:underline"
        >
          {h.name || h.code}
        </a>
        <p className="text-[11px] text-gray-500 dark:text-dark-300 mt-0.5 flex flex-wrap items-center gap-x-1.5">
          <span>Store #{h.storeId}</span>
          <span>·</span>
          <span>{h.source === "rescan" ? "Tempo re-check" : "user search"}</span>
          {h.aisle ? (
            <>
              <span>·</span>
              <span>aisle {h.aisle}</span>
            </>
          ) : null}
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${status.tone}`}>{status.text}</span>
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-[13px] font-mono font-bold tabular-nums text-gray-900 dark:text-white">
          {money(h.storePrice)} <span className="text-emerald-600 dark:text-emerald-400">-{h.discountPct}%</span>
        </p>
        <p className="text-[11px] text-gray-500 dark:text-dark-300 font-mono tabular-nums">
          was {money(h.refPrice)} · {h.stock} in stock
        </p>
      </div>
    </div>
  );
}

function GapsView({ data, retailer, search }: { data: SearchHarvestReport; retailer: Retailer; search: string }) {
  const q = search.trim().toLowerCase();
  const section = data.gaps?.[retailer];
  const items = (section?.items ?? []).filter((g) => matches(q, g.name, g.code, ...g.stores));
  const stores = (section?.stores ?? []).filter((s) => matches(q, s.storeId, s.label));
  if (items.length === 0 && stores.length === 0) {
    return <Empty text="No crawler gaps: every 60%+ hit at a followed store was already in the catalog." />;
  }
  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
      <div className={`${CARD} overflow-hidden divide-y divide-gray-100 dark:divide-dark-700`}>
        <div className="px-3 py-2">
          <p className={LABEL}>{items.length} items the crawler missed</p>
        </div>
        {items.map((g) => (
          <div key={g.code} className="flex items-start gap-3 px-3 py-2.5">
            <Thumb src={g.image} className="w-11 h-11 rounded-lg p-0.5" />
            <div className="min-w-0 flex-1">
              <a
                href={itemUrl(retailer, g.code)}
                target="_blank"
                rel="noreferrer"
                className="text-[12.5px] font-semibold leading-snug text-gray-900 dark:text-white line-clamp-1 hover:underline"
              >
                {g.name || g.code}
              </a>
              <p className="text-[11px] text-gray-500 dark:text-dark-300 mt-0.5">
                Missing at {g.stores.length} store{g.stores.length === 1 ? "" : "s"}
                {g.knownStores ? ` · crawled at ${g.knownStores}` : ""} · {g.stores.slice(0, 12).map((s) => `#${s}`).join(", ")}
                {g.stores.length > 12 ? "…" : ""}
              </p>
            </div>
            {g.bestPct > 0 && (
              <div className="text-right shrink-0">
                <p className="text-[13px] font-mono font-bold tabular-nums text-gray-900 dark:text-white">
                  {money(g.bestPrice)} <span className="text-emerald-600 dark:text-emerald-400">-{g.bestPct}%</span>
                </p>
                <p className="text-[11px] text-gray-500 dark:text-dark-300 font-mono tabular-nums">was {money(g.refPrice)}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className={`${CARD} overflow-hidden divide-y divide-gray-100 dark:divide-dark-700`}>
        <div className="px-3 py-2 flex items-center justify-between">
          <p className={LABEL}>Stores with the most misses</p>
          <p className={LABEL}>missed · had</p>
        </div>
        {stores.slice(0, 40).map((s) => (
          <div key={s.storeId} className="flex items-center gap-2 px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-semibold text-gray-900 dark:text-white">#{s.storeId}</p>
              <p className="text-[11px] text-gray-500 dark:text-dark-300 truncate">{s.label}</p>
            </div>
            <p className="text-[12px] font-mono tabular-nums text-gray-900 dark:text-white">
              {s.gaps} · <span className="text-gray-400">{s.known}</span>
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopView({ data, retailer, search }: { data: SearchHarvestReport; retailer: Retailer; search: string }) {
  const q = search.trim().toLowerCase();
  const rows = (data.topSearched?.[retailer] ?? []).filter((t) => matches(q, t.name, t.code));
  if (rows.length === 0) return <Empty text="No searches recorded in this window yet." />;
  return (
    <div className={`${CARD} overflow-hidden divide-y divide-gray-100 dark:divide-dark-700`}>
      <div className="px-3 py-2 flex items-center justify-between">
        <p className={LABEL}>{rows.length} most searched</p>
        <p className={LABEL}>searches · best markdown</p>
      </div>
      {rows.map((t, i) => (
        <div key={t.code} className="flex items-center gap-3 px-3 py-2.5">
          <span className="w-6 text-right text-[11px] font-mono text-gray-400 shrink-0">{i + 1}</span>
          <Thumb src={t.image} className="w-10 h-10 rounded-lg p-0.5" />
          <div className="min-w-0 flex-1">
            <a
              href={itemUrl(retailer, t.code)}
              target="_blank"
              rel="noreferrer"
              className="text-[12.5px] font-semibold leading-snug text-gray-900 dark:text-white line-clamp-1 hover:underline"
            >
              {t.name || t.code}
            </a>
            <p className="text-[11px] text-gray-500 dark:text-dark-300 mt-0.5">
              #{t.code}
              {t.hits ? ` · ${t.hits} hit${t.hits === 1 ? "" : "s"}` : ""}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[13px] font-mono font-bold tabular-nums text-gray-900 dark:text-white">{t.searches}</p>
            <p className={`text-[11px] font-mono tabular-nums ${t.bestPct >= data.minPct ? "text-emerald-600 dark:text-emerald-400" : "text-gray-500 dark:text-dark-300"}`}>
              {t.bestPct ? `-${t.bestPct}%` : "full price"}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function Segment({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-[12px] font-semibold transition-colors ${active
        ? "bg-gray-900 dark:bg-white text-white dark:text-dark-900"
        : "bg-white dark:bg-dark-800 text-gray-600 dark:text-dark-300 ring-1 ring-gray-200 dark:ring-dark-600 hover:text-gray-900 dark:hover:text-white"
        }`}
    >
      {children}
    </button>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className={`${CARD} px-4 py-3 min-w-0`}>
      <p className={LABEL}>{label}</p>
      <p className="text-xl font-mono font-bold tabular-nums mt-0.5 text-gray-900 dark:text-white">{value}</p>
      <p className="text-[11px] text-gray-500 dark:text-dark-300 mt-0.5 truncate">{sub}</p>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div className={`${CARD} py-10 px-4 text-center text-sm text-gray-500 dark:text-dark-400`}>{text}</div>;
}

function Thumb({ src, className }: { src?: string; className: string }) {
  return (
    <div className={`bg-white ring-1 ring-gray-100 dark:ring-dark-700 flex items-center justify-center overflow-hidden shrink-0 ${className}`}>
      {src ? <img src={src} alt="" loading="lazy" className="max-w-full max-h-full object-contain" /> : <span className="w-3 h-3 rounded-sm bg-gray-200 dark:bg-dark-600" />}
    </div>
  );
}
